import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { preserveInstitutionParams } from '../utils/navigation';
import Sidebar from './Sidebar';
import ConfirmDialog from './ConfirmDialog';
import ProjectPreview from './ModelPreview';
import { ModelInfo } from './ModelPreviewComponents/index';
import AppNavigation from './AppNavigation';
import { api } from '../services/api';

interface UploadedFile {
  id: string;
  original_filename: string;
  original_size_bytes: number;
  detected_mime_type: string;
  upload_time: string;
  status: string;
  project_name?: string;
  glb_path?: string;
  three_mf_path?: string;
  preview_url?: string;
  urls?: {
    original?: string;
    glb?: string;
    three_mf?: string;
  };
}

interface ProjectInfo {
  name: string;
  fileCount: number;
  lastModified: Date;
  thumbnailUrl?: string;
}

export default function PreviousProjects() {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<string | null>(null);
  const [currentProject, setCurrentProject] = useState<string | null>(null);
  const [projectModels, setProjectModels] = useState<ModelInfo[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showPreviewMode, setShowPreviewMode] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [renameProject, setRenameProject] = useState<{name: string; newName: string} | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => {
    // Initialize with value from localStorage
    return api.favorites.load();
  });
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [creditsRefreshTrigger, setCreditsRefreshTrigger] = useState(0);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Process files into project groups
  const processProjects = (files: UploadedFile[]) => {
    const projectMap = new Map<string, UploadedFile[]>();
    
    // Group files by project
    files.forEach(file => {
      if (file.project_name && file.project_name !== "Default Project") {
        if (!projectMap.has(file.project_name)) {
          projectMap.set(file.project_name, []);
        }
        projectMap.get(file.project_name)?.push(file);
      }
    });
    
    // Convert to project info objects
    const projectInfos: ProjectInfo[] = [];
    
    projectMap.forEach((projectFiles, projectName) => {
      // Sort files by upload date to find the most recent one
      const sortedFiles = [...projectFiles].sort((a, b) => 
        new Date(b.upload_time).getTime() - new Date(a.upload_time).getTime()
      );
      
      // Use the most recent file's date as the project's last modified date
      const lastModified = sortedFiles.length > 0 
        ? new Date(sortedFiles[0].upload_time) 
        : new Date();
      
      // Find a file with a preview URL if available
      const fileWithPreview = sortedFiles.find(file => file.preview_url);
      
      projectInfos.push({
        name: projectName,
        fileCount: projectFiles.length,
        lastModified,
        thumbnailUrl: fileWithPreview?.preview_url
      });
    });
    
    // Sort projects by last modified date (newest first)
    return projectInfos.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
  };

  // Load user's files when component mounts
  useEffect(() => {
    const loadFiles = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.files.list();
        setFiles(response.files);
        setProjects(processProjects(response.files));
      } catch (error) {
        console.error('Error loading files:', error);
        setError('Failed to load your projects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [currentUser]);

  const loadProjectModels = async (projectName: string) => {
    if (!currentUser) {
      navigate(preserveInstitutionParams('/login'));
      return;
    }
    
    setLoadingModels(true);
    setCurrentProject(projectName);
    setProjectModels([]);
    setShowPreviewMode(true);
    
    try {
      console.log(`[DEBUG] Loading models for project: ${projectName}`);
      const response = await api.files.getFilesByProject(projectName);
      console.log(`[DEBUG] Got ${response.files.length} files for project ${projectName}`);
      
      // Convert files to ModelInfo format
      const modelInfos: ModelInfo[] = [];
      
      for (const file of response.files) {
        console.log(`[DEBUG] Processing file: ${file.id}, status: ${file.status}, has glb_path: ${!!file.glb_path}`);
        if ((file.status === 'processed' || file.status === 'uploaded' || file.status === 'auto_oriented') && file.glb_path) {
          try {
            console.log(`[DEBUG] Getting GLB URL for file: ${file.id}, path: ${file.glb_path}`);
            // Always get a fresh URL for the GLB file to avoid expiration issues
            const glbUrl = await api.files.getDownloadUrl(file.id, 'glb');
            console.log(`[DEBUG] Successfully got GLB URL for file: ${file.id}`);
            
            modelInfos.push({
              id: file.id,
              filename: file.original_filename,
              size: file.original_size_bytes || 0,
              status: file.status,
              glbUrl: glbUrl,
              originalUrl: (file as any).urls?.original || '',
            });
          } catch (err) {
            console.error(`[DEBUG] Error getting URLs for file ${file.id}:`, err);
          }
        } else {
          console.log(`[DEBUG] Skipping file ${file.id}: status=${file.status}, has glb_path=${!!file.glb_path}`);
        }
      }
      
      console.log(`[DEBUG] Final modelInfos: ${modelInfos.length} models`);
      setProjectModels(modelInfos);
    } catch (error) {
      console.error('[DEBUG] Error loading project models:', error);
      setError('Failed to load project models');
    } finally {
      setLoadingModels(false);
    }
  };

  // Toggle dropdown menu for a specific project
  const toggleDropdown = (event: React.MouseEvent, projectName: string) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === projectName ? null : projectName);
  };

  // Handle rename project
  const handleRenameProject = (event: React.MouseEvent, projectName: string) => {
    event.stopPropagation();
    setActiveDropdown(null);
    setRenameProject({
      name: projectName,
      newName: projectName
    });
  };

  // Update project name input
  const handleRenameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (renameProject) {
      setRenameProject({
        ...renameProject,
        newName: e.target.value
      });
    }
  };

  // Confirm project rename
  const handleConfirmRename = async () => {
    if (renameProject && renameProject.name !== renameProject.newName) {
      try {
        setLoading(true);
        
        // Call API to rename the project on the backend
        const result = await api.projects.rename(renameProject.name, renameProject.newName);
        
        // Update local state to reflect the renamed project
        setProjects(prevProjects => prevProjects.map(project => 
          project.name === renameProject.name 
            ? { ...project, name: renameProject.newName } 
            : project
        ));
        
        setFiles(prevFiles => prevFiles.map(file => 
          file.project_name === renameProject.name
            ? { ...file, project_name: renameProject.newName }
            : file
        ));
        
        // Close the rename dialog
        setRenameProject(null);
        
        // Show a success notification
        setError(`Project successfully renamed to "${renameProject.newName}"`);
        
        // Clear the notification after 3 seconds
        setTimeout(() => {
          setError("");
        }, 3000);
      } catch (error) {
        console.error('Error renaming project:', error);
        setError('Failed to rename project. Please try again later.');
      } finally {
        setLoading(false);
      }
    } else {
      // If name didn't change, just close the dialog
      setRenameProject(null);
    }
  };

  // Cancel project rename
  const handleCancelRename = () => {
    setRenameProject(null);
  };

  // Handle delete project confirmation
  const handleDeleteProjectConfirm = (event: React.MouseEvent, projectName: string) => {
    event.stopPropagation();
    setActiveDropdown(null);
    setDeleteConfirmProject(projectName);
  };

  // Handler for confirmed project deletion
  const handleDeleteProject = async () => {
    if (deleteConfirmProject) {
      try {
        setLoading(true);
        // Call the API to delete the project from the backend
        await api.projects.delete(deleteConfirmProject, true);
        
        // Update the local state after successful deletion
        setFiles(prevFiles => prevFiles.filter(file => file.project_name !== deleteConfirmProject));
        setProjects(prevProjects => prevProjects.filter(project => project.name !== deleteConfirmProject));
        
        // Close the confirmation dialog
        setDeleteConfirmProject(null);
      } catch (error) {
        console.error('Error deleting project:', error);
        setError('Failed to delete project. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Cancel project deletion
  const handleCancelDelete = () => {
    setDeleteConfirmProject(null);
  };

  const handleBackToProjects = () => {
    setShowPreviewMode(false);
    setCurrentProject(null);
    setProjectModels([]);
  };

  const handleNewProject = () => {
    navigate(preserveInstitutionParams('/'));
  };

  // Navigate to previous project
  const navigateToPrevProject = () => {
    if (!currentProject || projects.length <= 1) return;
    
    // Find current project index
    const currentIndex = projects.findIndex(p => p.name === currentProject);
    if (currentIndex <= 0) return;
    
    // Load the previous project
    const prevProject = projects[currentIndex - 1].name;
    loadProjectModels(prevProject);
  };

  // Navigate to next project
  const navigateToNextProject = () => {
    if (!currentProject || projects.length <= 1) return;
    
    // Find current project index
    const currentIndex = projects.findIndex(p => p.name === currentProject);
    if (currentIndex === -1 || currentIndex >= projects.length - 1) return;
    
    // Load the next project
    const nextProject = projects[currentIndex + 1].name;
    loadProjectModels(nextProject);
  };

  // Format date to display in a readable way
  const formatDate = (date: Date) => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === now.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Toggle favorite status for a project using the API helper
  const toggleFavorite = (event: React.MouseEvent, projectName: string) => {
    event.stopPropagation();
    const newFavorites = api.favorites.toggle(favorites, projectName);
    setFavorites(newFavorites);
  };

  // Handle credits refresh when credits are deducted
  const handleCreditsChanged = () => {
    setCreditsRefreshTrigger(prev => prev + 1);
    fetchCurrentCredits(); // Also fetch updated credits
  };

  // Fetch current credits
  const fetchCurrentCredits = async () => {
    if (!currentUser) return;
    
    try {
      const planInfo = await api.subscriptions.getMyPlan();
      setCurrentCredits(planInfo.credits.credits_remaining);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  // Fetch credits when component mounts and when trigger changes
  useEffect(() => {
    if (currentUser) {
      fetchCurrentCredits();
    }
  }, [currentUser, creditsRefreshTrigger]);

  if (!currentUser) {
    // Redirect to login if not authenticated
    navigate(preserveInstitutionParams('/login'));
    return null;
  }

  return (
    <div className="app-container">
      <Sidebar currentUser={currentUser} />
      
      <div className="main-content with-sidebar">
        <AppNavigation 
          pageTitle={t('previousProjects.title')} 
          creditsRefreshTrigger={creditsRefreshTrigger} 
        />
        
        <div className="App">
          {error && (
            <p className={`error-message ${error.startsWith('Note:') ? 'note' : error.startsWith('Project successfully') ? 'success' : ''}`}>
              {error}
            </p>
          )}
          
          {loading ? (
            <div className="spinner-container">
              <div className="classic-spinner"></div>
              <h2 className="spinner-title">{t('previousProjects.loading.title')}</h2>
              <p className="spinner-subtitle">{t('previousProjects.loading.subtitle')}</p>
              <p className="spinner-text">{t('previousProjects.loading.text')}</p>
            </div>
          ) : showPreviewMode ? (
            // Preview Mode UI
            <div className="preview-mode">
              <div className="project-header-container">
                <button 
                  className="back-button"
                  onClick={handleBackToProjects}
                >
                  {t('previousProjects.navigation.back')}
                </button>
                <h2 className="project-title">{currentProject}</h2>
                <div className="navigation-buttons">
                  <button 
                    className="nav-button prev-button"
                    onClick={navigateToPrevProject}
                    disabled={projects.findIndex(p => p.name === currentProject) <= 0}
                    aria-label={t('previousProjects.navigation.previous')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button 
                    className="nav-button next-button"
                    onClick={navigateToNextProject}
                    disabled={projects.findIndex(p => p.name === currentProject) >= projects.length - 1}
                    aria-label={t('previousProjects.navigation.next')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 6L15 12L9 18" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              {currentProject && (
                <ProjectPreview 
                  projectName={currentProject} 
                  models={projectModels}
                  loading={loadingModels}
                  onCreditsChanged={handleCreditsChanged}
                  currentCredits={currentCredits}
                />
              )}
            </div>
          ) : (
            // Projects List UI
            <div>
              {projects.length === 0 ? (
                <div className="empty-state-wrapper">
                  <div className="empty-state">
                    <img 
                      src="/previous_icon.png" 
                      alt="No projects" 
                      className="empty-state-icon"
                    />
                    <p>{t('previousProjects.emptyState.message')}</p>
                    <Link to="/" className="button">{t('previousProjects.emptyState.button')}</Link>
                  </div>
                </div>
              ) : (
                <div>
                  {(() => {
                    // Separate projects into favorited and regular
                    const favoritedProjects = projects.filter(project => favorites.includes(project.name));
                    const regularProjects = projects.filter(project => !favorites.includes(project.name));
                    
                    // Sort each group by modification date (newest first)
                    favoritedProjects.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
                    regularProjects.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
                    
                    return (
                      <>
                        {/* Favorited Projects Section */}
                        {favoritedProjects.length > 0 && (
                          <div className="projects-section">
                            <div className="section-header">
                              <h2 className="section-title">{t('previousProjects.sections.favorited')}</h2>
                              <span className="section-count">{favoritedProjects.length} {favoritedProjects.length === 1 ? t('previousProjects.projectCount.project') : t('previousProjects.projectCount.projects')}</span>
                            </div>
                            <div className="project-list">
                              {favoritedProjects.map((project) => {
                                const isFavorite = favorites.includes(project.name);
                                return (
                                  <div 
                                    key={project.name} 
                                    className={`project-item ${isFavorite ? 'favorited' : ''}`} 
                                    onClick={() => loadProjectModels(project.name)}
                                    onMouseEnter={() => setHoveredProject(project.name)}
                                    onMouseLeave={() => setHoveredProject(null)}
                                  >
                                    <div className="project-content">
                                      <div className="project-icon">
                                        <img 
                                          src={hoveredProject === project.name ? "/folder-hover-icon.png" : "/folder-close-icon.png"}
                                          alt="Project folder icon" 
                                          style={{ display: 'block' }}
                                        />
                                      </div>
                                      <div className="project-details">
                                        <h3>{project.name}</h3>
                                        <span className="file-count">{project.fileCount} {project.fileCount === 1 ? t('previousProjects.fileCount.file') : t('previousProjects.fileCount.files')}</span>
                                      </div>
                                      <div className="project-actions">
                                        <div className="favorite-btn-container">
                                          <button 
                                            className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                                            onClick={(e) => toggleFavorite(e, project.name)}
                                            aria-label={isFavorite ? t('previousProjects.actions.removeFromFavorites') : t('previousProjects.actions.addToFavorites')}
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite ? "#07F77F" : "none"} xmlns="http://www.w3.org/2000/svg">
                                              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                                                stroke={isFavorite ? "#07F77F" : "#CCCCCC"} 
                                                strokeWidth="1.5" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                        <div className="three-dots-container">
                                          <div className="three-dots-menu">
                                            <button 
                                              className="three-dots-btn" 
                                              onClick={(e) => toggleDropdown(e, project.name)}
                                            >
                                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 3.25C8.41421 3.25 8.75 2.91421 8.75 2.5C8.75 2.08579 8.41421 1.75 8 1.75C7.58579 1.75 7.25 2.08579 7.25 2.5C7.25 2.91421 7.58579 3.25 8 3.25Z" fill="#CCCCCC"/>
                                                <path d="M8 8.75C8.41421 8.75 8.75 8.41421 8.75 8C8.75 7.58579 8.41421 7.25 8 7.25C7.58579 7.25 7.25 7.58579 7.25 8C7.25 8.41421 7.58579 8.75 8 8.75Z" fill="#CCCCCC"/>
                                                <path d="M8 14.25C8.41421 14.25 8.75 13.9142 8.75 13.5C8.75 13.0858 8.41421 12.75 8 12.75C7.58579 12.75 7.25 13.0858 7.25 13.5C7.25 13.9142 7.58579 14.25 8 14.25Z" fill="#CCCCCC"/>
                                              </svg>
                                            </button>
                                            {activeDropdown === project.name && (
                                              <div className="project-dropdown-menu">
                                                <div 
                                                  className="dropdown-item" 
                                                  onClick={(e) => handleRenameProject(e, project.name)}
                                                >
                                                  {t('previousProjects.actions.rename')}
                                                </div>
                                                <div 
                                                  className="dropdown-item delete-item" 
                                                  onClick={(e) => handleDeleteProjectConfirm(e, project.name)}
                                                >
                                                  {t('previousProjects.actions.delete')}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Regular Projects Section */}
                        {regularProjects.length > 0 && (
                          <div className="projects-section">
                            <div className="section-header">
                              <h2 className="section-title">{t('previousProjects.sections.all')}</h2>
                              <span className="section-count">{regularProjects.length} {regularProjects.length === 1 ? t('previousProjects.projectCount.project') : t('previousProjects.projectCount.projects')}</span>
                            </div>
                            <div className="project-list">
                              {regularProjects.map((project) => {
                                const isFavorite = favorites.includes(project.name);
                                return (
                                  <div 
                                    key={project.name} 
                                    className={`project-item ${isFavorite ? 'favorited' : ''}`} 
                                    onClick={() => loadProjectModels(project.name)}
                                    onMouseEnter={() => setHoveredProject(project.name)}
                                    onMouseLeave={() => setHoveredProject(null)}
                                  >
                                    <div className="project-content">
                                      <div className="project-icon">
                                        <img 
                                          src={hoveredProject === project.name ? "/folder-hover-icon.png" : "/folder-close-icon.png"}
                                          alt="Project folder icon" 
                                          style={{ display: 'block' }}
                                        />
                                      </div>
                                      <div className="project-details">
                                        <h3>{project.name}</h3>
                                        <span className="file-count">{project.fileCount} {project.fileCount === 1 ? t('previousProjects.fileCount.file') : t('previousProjects.fileCount.files')}</span>
                                      </div>
                                      <div className="project-actions">
                                        <div className="favorite-btn-container">
                                          <button 
                                            className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                                            onClick={(e) => toggleFavorite(e, project.name)}
                                            aria-label={isFavorite ? t('previousProjects.actions.removeFromFavorites') : t('previousProjects.actions.addToFavorites')}
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite ? "#07F77F" : "none"} xmlns="http://www.w3.org/2000/svg">
                                              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                                                stroke={isFavorite ? "#07F77F" : "#CCCCCC"} 
                                                strokeWidth="1.5" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                        <div className="three-dots-container">
                                          <div className="three-dots-menu">
                                            <button 
                                              className="three-dots-btn" 
                                              onClick={(e) => toggleDropdown(e, project.name)}
                                            >
                                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 3.25C8.41421 3.25 8.75 2.91421 8.75 2.5C8.75 2.08579 8.41421 1.75 8 1.75C7.58579 1.75 7.25 2.08579 7.25 2.5C7.25 2.91421 7.58579 3.25 8 3.25Z" fill="#CCCCCC"/>
                                                <path d="M8 8.75C8.41421 8.75 8.75 8.41421 8.75 8C8.75 7.58579 8.41421 7.25 8 7.25C7.58579 7.25 7.25 7.58579 7.25 8C7.25 8.41421 7.58579 8.75 8 8.75Z" fill="#CCCCCC"/>
                                                <path d="M8 14.25C8.41421 14.25 8.75 13.9142 8.75 13.5C8.75 13.0858 8.41421 12.75 8 12.75C7.58579 12.75 7.25 13.0858 7.25 13.5C7.25 13.9142 7.58579 14.25 8 14.25Z" fill="#CCCCCC"/>
                                              </svg>
                                            </button>
                                            {activeDropdown === project.name && (
                                              <div className="project-dropdown-menu">
                                                <div 
                                                  className="dropdown-item" 
                                                  onClick={(e) => handleRenameProject(e, project.name)}
                                                >
                                                  {t('previousProjects.actions.rename')}
                                                </div>
                                                <div 
                                                  className="dropdown-item delete-item" 
                                                  onClick={(e) => handleDeleteProjectConfirm(e, project.name)}
                                                >
                                                  {t('previousProjects.actions.delete')}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Confirmation Dialog for Project Deletion */}
      <ConfirmDialog
        isOpen={deleteConfirmProject !== null}
        title={t('previousProjects.modals.deleteProject.title')}
        message={t('previousProjects.modals.deleteProject.message', { projectName: deleteConfirmProject })}
        onConfirm={handleDeleteProject}
        onCancel={handleCancelDelete}
        confirmText={t('previousProjects.modals.deleteProject.confirm')}
      />

      {/* Rename Project Dialog */}
      {renameProject && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{t('previousProjects.modals.renameProject.title')}</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="project-name">{t('previousProjects.modals.renameProject.label')}</label>
                <input
                  type="text"
                  id="project-name"
                  className="project-name-input"
                  value={renameProject.newName}
                  onChange={handleRenameInputChange}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleCancelRename}>
                {t('previousProjects.modals.renameProject.cancel')}
              </button>
              <button className="rename-btn" onClick={handleConfirmRename}>
                {t('previousProjects.modals.renameProject.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 